import { ScrollView } from "react-native";
import { Info, type LucideIcon } from "lucide-react-native";
import {
  Dialog,
  DialogBodyText,
  DialogTitleText,
  IconButton,
  MoonDetailRow,
} from "@louise/moon-ui";
import { EMBER } from "../../onboarding/theme";

/** A titled section names its icon, like every label over a value. */
export type InfoSection =
  | { title: string; icon: LucideIcon; body: string }
  | { title?: undefined; icon?: undefined; body: string };

type Props = {
  visible: boolean;
  onClose: () => void;
  title: string;
  sections: InfoSection[];
};

/**
 * Small (i) button for the top-right corner of cards.
 */
export function InfoButton({
  onPress,
  accessibilityLabel = "View more information",
  action,
  testID,
}: {
  onPress: () => void;
  accessibilityLabel?: string;
  /** Stable analytics identifier, required when `accessibilityLabel` is translated. */
  action?: string;
  testID?: string;
}) {
  return (
    <IconButton
      accessibilityLabel={accessibilityLabel}
      action={action}
      testID={testID}
      onPress={onPress}
      hitSlop={10}
      size={28}
    >
      <Info size={14} color={EMBER.colors.textFaint} strokeWidth={1.8} />
    </IconButton>
  );
}

export default function InfoSheet({
  visible,
  onClose,
  title,
  sections,
}: Props) {
  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      closeAction="close_info_sheet"
      backdropPressLabel="dismiss_info_sheet_backdrop"
      contentStyle={{
        paddingTop: 24,
        paddingHorizontal: 24,
        paddingBottom: 24,
      }}
    >
      <DialogTitleText style={{ marginBottom: 16, paddingRight: 32 }}>
        {title}
      </DialogTitleText>

      <ScrollView
        style={{ maxHeight: 400 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ gap: 16 }}
      >
        {sections.map((section, i) =>
          section.title ? (
            <MoonDetailRow
              key={i}
              icon={section.icon}
              label={section.title}
              value={
                <DialogBodyText style={{ marginTop: 0 }}>
                  {section.body}
                </DialogBodyText>
              }
            />
          ) : (
            <DialogBodyText key={i} style={{ marginTop: 0 }}>
              {section.body}
            </DialogBodyText>
          ),
        )}
      </ScrollView>
    </Dialog>
  );
}
